// app.js - Main application controller.

import { AUTH } from './auth.js';
import { CALIBRATION } from './calibration.js';
import { CODE_GENERATOR } from './code-generator.js';
import { CONFIG } from './config.js';
import { clubForSession, applyClubTheme, applyNeutralTheme, markDeveloperSession, clubById } from './clubs.js';
import { DB } from './db.js';
import { DRAFT } from './draft.js';
import { getDomains, loadNlDescriptors, loadRubricOverrides } from './evaluation-data.js';
import EVAL_NL from './i18n/eval-descriptors-nl.json';
import { PLANNER } from './evaluation-planner.js';
import { EVALUATION } from './evaluation.js';
import { isYoungTeam } from './young-age-data.js';
import { BASELINE_TESTER } from './baseline-tester.js';
import { LINEUP } from './lineup.js';
import { SELF_REFLECTION } from './self-reflection.js';
import { HEALTH } from './health-check.js';
import { STAFF_EVAL } from './staff-eval.js';
import { STAFF_NOTES } from './staff-notes.js';
import { hasGamePicks } from './game-picks-data.js';
import { skillSlugForLabel, practicesForSkill, practiceBandForTeamId, practiceText } from './practice-data.js';
import PRACTICE_SKILLS from './i18n/practice-skills.json';
import { t } from './strings.js';
import { STAFF } from './staff.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';
import { lazyScreen } from './lazy-screen.js';

// Lazily-loaded screens - each compiles to its own chunk, fetched on first use,
// so the main bundle ships only the shell plus whatever the current user opens.
// Call sites are unchanged (the proxy delegates - see lazy-screen.js). Left
// eager above: CALIBRATION and BASELINE_TESTER, which read a method's return
// value synchronously.
const DASHBOARD              = lazyScreen(() => import('./dashboard.js'), 'DASHBOARD');
const SQUAD_GRID             = lazyScreen(() => import('./squad-grid.js'), 'SQUAD_GRID');
const PDF_PLAYER             = lazyScreen(() => import('./pdf-player.js'), 'PDF_PLAYER');
const PDF_CERTIFICATE        = lazyScreen(() => import('./pdf-certificate.js'), 'PDF_CERTIFICATE');
const PDF_REPORT             = lazyScreen(() => import('./pdf-report.js'), 'PDF_REPORT');
const BASELINE               = lazyScreen(() => import('./baseline.js'), 'BASELINE');
const COACH_HUB              = lazyScreen(() => import('./coach-hub.js'), 'COACH_HUB');
const DTA_ADMIN              = lazyScreen(() => import('./dta-admin.js'), 'DTA_ADMIN');
const DTA_ARTICLE            = lazyScreen(() => import('./dta-article-view.js'), 'DTA_ARTICLE');
const PRACTICE_VIEW          = lazyScreen(() => import('./practice-view.js'), 'PRACTICE_VIEW');
const EXERCISE_VIEW          = lazyScreen(() => import('./exercise-view.js'), 'EXERCISE_VIEW');
const PLAYER_ACCESS          = lazyScreen(() => import('./player-access.js'), 'PLAYER_ACCESS');
const SHARE_DASH             = lazyScreen(() => import('./share-dashboard.js'), 'SHARE_DASH');
const VICTORY                = lazyScreen(() => import('./victory-page.js'), 'VICTORY');
const TEAM_FILES             = lazyScreen(() => import('./team-files.js'), 'TEAM_FILES');
const GRADING_PANEL          = lazyScreen(() => import('./grading-panel.js'), 'GRADING_PANEL');
const SQUAD_REPLIES          = lazyScreen(() => import('./squad-replies.js'), 'SQUAD_REPLIES');
const STAFF_ACCESS           = lazyScreen(() => import('./staff-access.js'), 'STAFF_ACCESS');
const STAFF_APPLY            = lazyScreen(() => import('./staff-apply.js'), 'STAFF_APPLY');
const PLAYER_PAGE            = lazyScreen(() => import('./player-page.js'), 'PLAYER_PAGE');
const RANKING                = lazyScreen(() => import('./ranking.js'), 'RANKING');
const PLAYER_HISTORY         = lazyScreen(() => import('./player-history-view.js'), 'PLAYER_HISTORY');
const SELECTION_REPORT       = lazyScreen(() => import('./selection-analysis-report.js'), 'SELECTION_REPORT');
const MASTER_ANALYSIS_REPORT = lazyScreen(() => import('./master-analysis-report.js'), 'MASTER_ANALYSIS_REPORT');
const GAME_PICKS             = lazyScreen(() => import('./game-picks.js'), 'GAME_PICKS');
const SELECTION_COACH        = lazyScreen(() => import('./selection-coach.js'), 'SELECTION_COACH');
const SELECTION              = lazyScreen(() => import('./selection.js'), 'SELECTION');
const RUBRIC_EDITOR          = lazyScreen(() => import('./rubric-editor.js'), 'RUBRIC_EDITOR');
const YOUNG_EVAL             = lazyScreen(() => import('./young-eval.js'), 'YOUNG_EVAL');

// Load the Dutch rubric overlay so getDescriptors/localize* can return Dutch.
// Import runs before this executes, so the JSON is ready.
loadNlDescriptors(EVAL_NL);

// The shortest reason a support login will accept, in step with the same floor
// in the developer-auth function and the developer_access_log_reason_given
// constraint. See _showDeveloperClubPicker.
const MIN_DEVELOPER_REASON = 3;

export const APP = (() => {

  // ── STATE ────────────────────────────────────────────────────────────────────

  let _currentTeam       = null;
  let _currentPlayers    = [];
  let _evalRecords       = {};   // { [playerId]: evaluation[] }
  let _allTeams          = [];
  let _pendingCodeRecord = null; // holds code record between gate and name screen
  let _homeLineFilter    = 'all'; // 'all' | 'girls' | 'boys'
  let _memberCounts      = null;  // team_id -> player count, for the home grid

  // ── BOOT ─────────────────────────────────────────────────────────────────────

  // Entry point.
  async function boot() {
    AUTH.init();

    // The product brand owns the front door until a code says otherwise.
    applyNeutralTheme();

    // The current-season setting is refreshed from the DB AFTER login (the
    // `seasons` table is not readable anonymously, so calling it on the gate
    // would 401). Until then the app uses the current season hardcoded in
    // config.js. The logged-in branch below does the refresh once we have a token.

    // Check for code in URL query string
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');

    if (urlCode) {
      // Pre-fill and auto-submit the gate
      await _handleCode(urlCode);
      return;
    }

    // Restore existing session if valid
    if (AUTH.isLoggedIn()) {
      // A returning session is already past the door, so it wears its club
      // immediately; without this the app would render in neutral colours.
      applyClubTheme(clubForSession(AUTH.getSession(), AUTH.getCode()));
      await _refreshCurrentSeason();   // now that we have a token
      _routeAfterLogin();
      return;
    }

    // Default: show gate
    UI.showScreen('screen-gate');
    document.getElementById('gate-input')?.focus();
  }

  // Pull the active season from the DB and overwrite CONFIG.season.current.
  // A director who has explicitly switched season keeps their choice: nearly
  // every screen reads CONFIG.season.current at call time, so without this the
  // rollover made LAST season's evaluations unreachable in the app even though
  // the rows were all still there.
  const SEASON_PREF_KEY = 'khc_season_override';

  async function _refreshCurrentSeason() {
    try {
      const rows = await DB.seasons.getCurrent();
      if (rows && rows.length) {
        const s = rows[0];
        CONFIG.season.current   = s.id;
        CONFIG.season.liveId    = s.id;   // the real season, never overridden
        CONFIG.season.label     = s.label || s.id;
        CONFIG.season.startDate = s.start_date || CONFIG.season.startDate;
        CONFIG.season.endDate   = s.end_date   || CONFIG.season.endDate;
      }
    } catch { /* keep whatever's in CONFIG */ }
    // A stored override wins, so the director stays where they were looking.
    try {
      const saved = localStorage.getItem(SEASON_PREF_KEY);
      if (saved) {
        const all = await DB.seasons.getAll().catch(() => []);
        const s = (all || []).find(x => x.id === saved);
        if (s) {
          CONFIG.season.current = s.id;
          CONFIG.season.label   = s.label || s.id;
        }
      }
    } catch { /* no storage: stay on the DB's current season */ }
  }

  // Season switcher on the home badge. Only the director and co-director see
  // it; coaches and families always work in the live season.
  async function _renderSeasonBadge() {
    const badge = document.querySelector('#screen-home .season-badge');
    if (!badge) return;
    const isDir = AUTH.isDirector() || AUTH.isCoDirector();
    const label = CONFIG.season.label || CONFIG.season.current;
    const live  = CONFIG.season.current === CONFIG.season.liveId;
    badge.innerHTML =
      `<span class="season-badge__dot"></span>${UTILS.escapeHtml(label)} · Director` +
      (isDir ? ` <button class="season-badge__switch" id="btn-season-switch"
                   aria-label="${live ? 'Switch season' : 'Viewing a past season, switch'}"
                 >${live ? 'Change' : 'Past season'}</button>` : '');
    if (!isDir) return;
    document.getElementById('btn-season-switch')?.addEventListener('click', async () => {
      const all = await DB.seasons.getAll().catch(() => []);
      const ids = (all || []).map(s => s.id).filter(Boolean).sort().reverse();
      if (!ids.length) { UI.toast('No seasons found.', 'info'); return; }
      _openSeasonModal(ids, all);
    });
  }

  function _openSeasonModal(ids, all) {
    const esc = UTILS.escapeHtml;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal">
        <div class="modal__title">${esc(t('season.pick'))}</div>
        <div class="modal__body">
          <div class="sel-detail-hint" style="margin-bottom:10px;">
            Everything below follows this choice: the Dragons Score, team lists,
            evaluations and exports. Past seasons are read-only in practice.
          </div>
          ${ids.map(id => `
            <button class="btn ${id === CONFIG.season.current ? 'btn--primary' : 'btn--secondary'}"
                    data-season="${esc(id)}" style="width:100%; margin-bottom:8px;">
              ${esc(id)}${id === CONFIG.season.liveId ? ' · live' : ''}
            </button>`).join('')}
        </div>
        <div class="modal__buttons"><button class="modal-btn" data-action="cancel">Cancel</button></div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-action="cancel"]').onclick = close;
    overlay.querySelectorAll('[data-season]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.season;
      const s = (all || []).find(x => x.id === id);
      CONFIG.season.current = id;
      CONFIG.season.label   = (s && s.label) || id;
      try { localStorage.setItem(SEASON_PREF_KEY, id); } catch { /* ignore */ }
      close();
      showHome();   // re-reads every season-scoped list
    }));
  }

  // ── GATE ─────────────────────────────────────────────────────────────────────

  async function _submitGate() {
    const input = document.getElementById('gate-input');
    const errorEl = document.getElementById('gate-error');
    const code = input?.value?.trim() || '';
    errorEl.textContent = '';

    if (!code) return;

    try {
      const result = await AUTH.validateCode(code);

      if (!result.valid) {
        errorEl.textContent =
          result.reason === 'expired_code'    ? 'This code has expired. Please contact your director for a new one.'
        : result.reason === 'not_yet_active'  ? 'This code is not active yet. Please use it during its scheduled window.'
        : result.reason === 'no_active_window'? 'No evaluation pillar is open right now. Check back during the next scheduled window.'
        : result.reason === 'trainer_archived'? 'This trainer code has been archived. Ask the director to restore it or issue a new one.'
        : result.reason === 'network'         ? 'You appear to be offline. Check your connection and try again.'
        : 'Invalid code. Please check and try again.';
        return;
      }

      await _handleCode(code, result);
    } catch {
      errorEl.textContent = 'Connection error. Please try again.';
    }
  }

  /**
   * Which club, and why.
   *
   * The reason is not a formality. It is the difference between a log that
   * says somebody was here and one that says what they were doing, and it is
   * the only field a club would actually care to read. Required since 31 Oct
   * 2026, after two of the first eight logged sessions turned out to say
   * nothing: the edge function refuses the login without one and the column is
   * NOT NULL, so this check is the explanation rather than the enforcement.
   *
   * `why` carries the typed text back in when the reason was too short, since
   * UI.modal closes on any button and retyping a reason as a punishment for
   * giving a thin one is how a field gets filled with "x".
   */
  function _showDeveloperClubPicker(label, clubs, why = '') {
    const esc = UTILS.escapeHtml;
    if (!clubs.length) {
      UI.modal({ title: 'No clubs', body: 'There are no clubs to enter.',
                 buttons: [{ label: 'Close' }] });
      return;
    }
    UI.modal({
      title: `Developer access (${esc(label)})`,
      // One source line per element on purpose: .modal__body is
      // white-space: pre-line, so a sentence wrapped across lines here breaks
      // mid-phrase on screen.
      html: `<div style="margin-bottom:12px;">Choose a club. Entering one is recorded: who, which club, when, and the reason you give here. The reason is required.</div>`
        + `<input id="dev-why" class="field" type="text" maxlength="200" value="${esc(why)}" placeholder="Reason, e.g. setting up their first season">`,
      buttons: [
        ...clubs.map(c => ({
          label: `Enter ${c.name || c.id}`,
          action: async () => {
            const typed = document.getElementById('dev-why')?.value?.trim() || '';
            if (typed.length < MIN_DEVELOPER_REASON) {
              UI.toast('Say what the session is for. It goes in the club\'s access log.', 'info');
              _showDeveloperClubPicker(label, clubs, typed);
              return;
            }
            const r = await AUTH.enterClubAsDeveloper(c.id, typed);
            if (!r.valid) {
              UI.toast(
                r.reason === 'log_failed'
                  ? 'Could not record the access, so no session was started.'
                : r.reason === 'missing_reason'
                  ? 'The reason was refused. Give a little more detail.'
                : 'Could not enter that club.', 'error');
              if (r.reason === 'missing_reason') _showDeveloperClubPicker(label, clubs, typed);
              return;
            }
            applyClubTheme(clubForSession(AUTH.getSession(), ''));
            await _routeAfterLogin();
          },
        })),
        { label: 'Cancel' },
      ],
    });
  }

  async function _handleCode(code, validationResult = null) {
    // Validate if not already done
    const result = validationResult || await AUTH.validateCode(code);

    // Wear the club here rather than at the gate, because a code can also
    // arrive by ?code= in the URL. Only ever AFTER the code validates: theming
    // on keystroke would let a visitor paint the app as any club by typing.
    if (result.valid) applyClubTheme(clubForSession(AUTH.getSession(), code));

    if (!result.valid) {
      UI.showScreen('screen-gate');
      document.getElementById('gate-error').textContent =
        result.reason === 'expired_code'    ? 'This code has expired. Contact your director for a new one.'
      : result.reason === 'not_yet_active'  ? 'This code is not active yet. Please use it during its scheduled window.'
      : result.reason === 'no_active_window'? 'No evaluation pillar is open right now. Check back during the next scheduled window.'
      : result.reason === 'trainer_archived'? 'This trainer code has been archived. Ask the director to restore it or issue a new one.'
      : result.reason === 'network'         ? 'You appear to be offline. Check your connection and try again.'
      : 'Invalid code. Please check and try again.';
      return;
    }

    // Support access. The code alone has minted nothing yet: this asks which
    // club, and THAT is the step the server logs. Deliberately a modal rather
    // than a quiet redirect, because entering someone's club should be a thing
    // you did, not a thing that happened.
    if (result.isDeveloper) {
      _showDeveloperClubPicker(result.label, result.clubs);
      return;
    }

    if (result.isDirector) {
      AUTH.setDirectorSession();
      // Through the router like every other login, not straight to showHome().
      // Going direct skipped the one place that applies the session's club, so
      // Victory's director kept Dragons' colours AND, because the club decides
      // which teams the lists ask for, an empty Match replies screen.
      await _routeAfterLogin();
    } else if (result.isSelection || result.isPlayer || result.isStaff ||
               result.isBaselineTester || result.isCoDirector || result.isSquad) {
      // Selection, player, staff, baseline-tester, co-director and squad codes
      // are all resolved server-side; auth.js already adopted the session.
      // A squad code names nobody: the squad page asks who you are instead,
      // which is why it must not reach the "enter your name" screen below.
      _routeAfterLogin();
    } else {
      // Store code record temporarily for name screen
      _pendingCodeRecord = result.record;
      UI.showScreen('screen-name');
      document.getElementById('name-input')?.focus();
    }
  }

  // ── NAME SCREEN ───────────────────────────────────────────────────────────────

  async function _submitName() {
    const input   = document.getElementById('name-input');
    const errorEl = document.getElementById('name-error');
    const name    = input?.value?.trim() || '';
    errorEl.textContent = '';

    if (!name || name.length < 2) {
      errorEl.textContent = 'Please enter your full name.';
      return;
    }

    const codeRecord = _pendingCodeRecord;
    if (!codeRecord) {
      errorEl.textContent = 'Session expired. Please enter your code again.';
      UI.showScreen('screen-gate');
      return;
    }

    const result = await AUTH.setCoachSession(name, codeRecord);
    if (!result?.ok) {
      errorEl.textContent = result?.error === 'expired_code'
        ? 'This code has expired. Please contact your director.'
        : 'Could not start session. Please try again.';
      return;
    }
    _pendingCodeRecord = null;

    _routeAfterLogin();
  }

  // ── ROUTING ──────────────────────────────────────────────────────────────────

  async function _routeAfterLogin() {
    // Wear the club of the session that was just adopted. The gate already
    // applies a club when the code validates, but a DIRECTOR code is a two
    // step login: validateCode holds the session back until the name screen is
    // past, so at that first point there is nothing signed to read and the
    // code's spelling is all there is. That made Victory's director arrive on
    // a Dragons-coloured page. Re-applying here is the one place every login
    // type passes through with its session in hand.
    applyClubTheme(clubForSession(AUTH.getSession(), AUTH.getCode()));

    // Say so, loudly and permanently, when this is support access rather than
    // the club's own director. The database cannot tell the difference by
    // design; the person looking at the screen must be able to.
    const sess = AUTH.getSession();
    markDeveloperSession(
      AUTH.isDeveloper() ? (sess?.developerLabel || 'developer') : null,
      sess?.clubName || clubById(sess?.clubId)?.name || sess?.clubId,
    );

    // This club's rubric overrides, before the first screen draws, so nobody
    // ever reads a descriptor that is about to change under them. Awaited
    // rather than fired off: it is one request, it returns an empty array for
    // every club that has customised nothing, and a screen rendered with the
    // standard wording and then silently rewritten is worse than a beat's
    // wait. A failure here is not fatal, it just means the standard wording,
    // which is what the club had anyway.
    try {
      loadRubricOverrides(await DB.rubric.overridesFor());
    } catch (err) {
      console.warn('Rubric overrides unavailable, using the standard wording:', err);
      loadRubricOverrides(null);
    }

    if (AUTH.isSquad()) {
      // A whole squad on one code: their club's own page asks who they are.
      return VICTORY.show();
    } else if (AUTH.isDirector() || AUTH.isCoDirector()) {
      return showHome();   // showHome() routes the co-director to her trimmed home
    } else if (AUTH.isPlayer()) {
      // Player/parent - straight to their own published evaluations.
      return PLAYER_PAGE.showHome();
    } else if (AUTH.isStaff()) {
      // Trainer/coach - their Coach Hub.
      return COACH_HUB.showHome();
    } else if (AUTH.isBaselineTester()) {
      return BASELINE_TESTER.showHome();
    } else if (AUTH.isSelection()) {
      // Selection trainer - one code, many sessions: pick a session first.
      return SELECTION_COACH.showSessions();
    } else {
      // Coach or feedback - go straight to their team's player list
      const team = AUTH.getTeam();
      if (team) {
        return showPlayerList(team);
      }
      UI.showScreen('screen-gate');
    }
  }

  // ── HOME (director) ──────────────────────────────────────────────────────────

  // The co-director shares the director home (badge already reads "Director")
  // but keeps only her own tools - Dragons Score, Player tracking, the Dashboard
  // and the Master analysis export - plus the team browser below, which is her
  // "Evaluations" entry: tap a team, tap a player to read the evaluations and,
  // if she wants, add one on the spot.
  // Everything administrative is hidden; RLS is the real boundary underneath.
  function _gateHomeForRole() {
    const codir = AUTH.isCoDirector();
    // btn-master-analysis added 2026-07-24 (director: "The co-director should
    // have access too"). It is a read-only export of what she can already see
    // on the Dragons Score screen, and the view behind it is director+codirector
    // gated, so this widens no data boundary.
    //
    // btn-dashboard added 2026-08-08, same reasoning. showOverview() reads teams,
    // evaluations, memberships, ceilings, ratings and players, and she already
    // held SELECT on all six. It is the summary sitting on top of the Score she
    // already had, so this opens a screen rather than a data boundary. Note that
    // dashboard.js also hosts the squad and code managers; those stay hidden
    // because they are behind their own buttons, and RLS refuses her anyway.
    const keep = new Set(['btn-ranking', 'btn-tracking', 'btn-master-analysis', 'btn-dashboard', 'btn-squad-grid']);
    document.querySelectorAll('#screen-home .home-nav button').forEach(b => {
      b.style.display = (!codir || keep.has(b.id)) ? '' : 'none';
    });
    document.querySelectorAll('#screen-home .home-nav__grid').forEach(grid => {
      const anyVisible = [...grid.querySelectorAll('button')].some(b => b.style.display !== 'none');
      grid.style.display = anyVisible ? '' : 'none';
      const label = grid.previousElementSibling;
      if (label && label.classList.contains('home-nav__label')) label.style.display = anyVisible ? '' : 'none';
    });
  }

  async function showHome() {
    UI.showScreen('screen-home');
    _gateHomeForRole();   // trim the menu for the limited co-director
    _renderSeasonBadge(); // season switcher, so past seasons stay reachable
    // Always refresh - new teams from an import (or a deletion in Manage)
    // wouldn't otherwise show up until the tab is reloaded.
    try {
      _allTeams = await DB.teams.getAll() || [];
    } catch {
      if (!_allTeams.length) {
        UI.toast('Could not load teams', 'error');
        return;
      }
      // Network blip - keep stale list rather than wiping the screen.
    }
    _memberCounts = null;   // re-read on every visit: imports change this
    _renderTeamGrid();
  }

  async function _renderTeamGrid() {
    const grid = document.getElementById('team-grid');
    if (!grid) return;

    // Player counts come first, because they decide WHICH cards get drawn: a
    // team with nobody in it is not shown at all. Cached across line-filter
    // clicks, cleared on every return to home so an import shows up.
    if (!_memberCounts) {
      grid.innerHTML = `<div class="sel-detail-hint">${UTILS.escapeHtml(t('common.loading'))}</div>`;
      const members = await DB.memberships
        .getAllForSeason(CONFIG.season.current).catch(() => []);
      _memberCounts = {};
      members.forEach(m => { _memberCounts[m.team_id] = (_memberCounts[m.team_id] || 0) + 1; });
    }
    const counts = _memberCounts;
    grid.innerHTML = '';

    // Hook up the Girls/Boys/All line filter pill. Auto-hides when only one
    // line is present in the data, so single-line clubs see no UI clutter.
    _homeLineFilter = UI.setupLineFilter('home-line-filter', _allTeams, _homeLineFilter, v => {
      _homeLineFilter = v;
      _renderTeamGrid();
    });

    const esc = UTILS.escapeHtml;

    // A team with nobody in it is not shown. Half the club's team ids exist
    // before anyone has been placed in them, and an empty card is a dead end:
    // tapping it opens a player list with nothing in it. They come back the
    // moment a player is assigned.
    const peopled = _allTeams.filter(t => (counts[t.id] || 0) > 0);
    if (_allTeams.length && !peopled.length) {
      grid.innerHTML = `<div class="empty-state"><p class="empty-state__message">${esc(t('home.noPlayersYet'))}</p></div>`;
      return;
    }

    const linesPresent = new Set(peopled.map(t => t.line || 'girls'));
    const multiLine    = linesPresent.size > 1;

    // Decide which lines to render based on the pill.
    const renderLines = _homeLineFilter === 'all'
      ? ['girls', 'boys'].filter(l => linesPresent.has(l))
      : [_homeLineFilter].filter(l => linesPresent.has(l));

    const lineLabels = { girls: 'Girls', boys: 'Boys' };

    renderLines.forEach((line, idx) => {
      const lineTeams = peopled.filter(t => (t.line || 'girls') === line);
      if (!lineTeams.length) return;

      // Show a line heading + divider when both lines render in one view.
      // Single-line view (multi-line club filtered to one, or single-line
      // club) skips the heading entirely - same look as before for clubs
      // that have only one line.
      if (multiLine && _homeLineFilter === 'all') {
        if (idx > 0) {
          const hr = document.createElement('div');
          hr.className = 'home-section-divider';
          grid.appendChild(hr);
        }
        const heading = document.createElement('div');
        heading.className = 'home-section-heading';
        heading.innerHTML = `<span class="home-section-heading__dot home-section-heading__dot--${line}"></span>${esc(lineLabels[line] || line)}`;
        grid.appendChild(heading);
      }

      const _dash = s => (s || '').replace(/[–—]/g, '-'); // en/em-dash -> hyphen
      CONFIG.ageGroups.forEach(ageGroup => {
        const ageTeams = lineTeams.filter(t => _dash(t.age_group) === _dash(ageGroup));
        ageTeams.forEach(team => {
          const ageClass = ageGroup.replace(/[^a-z0-9]/gi, '-').toLowerCase();
          const isBoys   = (team.line || 'girls') === 'boys';
          const lineTag  = isBoys
            ? '<span class="team-line-tag team-line-tag--boys">Boys</span>'
            : '<span class="team-line-tag team-line-tag--girls">Girls</span>';
          const card = document.createElement('div');
          card.className = `team-card team-card--${ageClass}`;
          card.innerHTML = `
            <div class="team-card__name">${esc(team.id)}${lineTag}</div>
            <div class="team-card__age">${esc(team.age_group)}</div>
            <div class="team-card__count">${(() => { const n = counts[team.id] || 0; return `${n} player${n !== 1 ? 's' : ''}`; })()}</div>
          `;
          card.addEventListener('click', () => showPlayerList(team));
          grid.appendChild(card);
        });
      });
    });

    // Fill in counts once the bulk query lands.
  }

  // ── PLAYER LIST ───────────────────────────────────────────────────────────────

  async function showPlayerList(team) {
    if (team) _currentTeam = team;
    UI.showScreen('screen-players');

    // Header - show the team's line in the subtitle when it's a Boys team
    // (Girls is implicit/default - no subtitle clutter when single-line).
    document.getElementById('team-title').textContent = _currentTeam.id;
    const lineLabel = (_currentTeam.line || 'girls') === 'boys' ? ' · Boys' : ' · Girls';
    document.getElementById('team-subtitle').textContent = _currentTeam.age_group + lineLabel;

    // Coach banner
    if (AUTH.isCoach() || AUTH.isFeedback()) {
      UI.setCoachBanner(
        true,
        `${AUTH.getCoachName()} · ${_currentTeam.id}${AUTH.isFeedback() ? ' · Feedback only' : ''}`
      );
      document.getElementById('back-from-players').style.display = 'none';
    } else if (AUTH.isStaff()) {
      // Coach evaluating with their personal code - banner + a back button that
      // returns to their Coach Hub (not the director home).
      UI.setCoachBanner(true, `${AUTH.getStaffName()} · ${_currentTeam.id}`);
      document.getElementById('back-from-players').style.display = 'flex';
    } else {
      UI.setCoachBanner(false);
      document.getElementById('back-from-players').style.display = 'flex';
    }

    // "After the game" picks - for whoever coaches this team (coach/feedback or
    // a personal-code staff coach), not the director.
    const gpBtn = document.getElementById('btn-game-picks');
    if (gpBtn) {
      const isGameCoach = AUTH.isCoach() || AUTH.isFeedback() || AUTH.isStaff();
      // U7 and U8 have no post-game picks at all: the club does not rank
      // children that young against each other in either direction.
      gpBtn.style.display = (isGameCoach && hasGamePicks(_currentTeam?.id)) ? '' : 'none';
      gpBtn.onclick = () => GAME_PICKS.show(_currentTeam, () => showPlayerList(_currentTeam));
    }

    // Director-only quick "Calibrate this team" entry on the team's player list.
    const calBtn = document.getElementById('btn-calibrate-this-team');
    if (calBtn) {
      if (AUTH.isDirector()) {
        calBtn.style.display = '';
        calBtn.onclick = () => CALIBRATION.showTeam(_currentTeam.id);
      } else {
        calBtn.style.display = 'none';
      }
    }

    UI.setLoading('player-list');

    try {
      // Load players and evaluations in parallel
      const [members, evals] = await Promise.all([
        DB.players.getByTeam(_currentTeam.id, CONFIG.season.current),
        DB.evaluations.getByTeam(_currentTeam.id, CONFIG.season.current),
      ]);

      // Build sorted player list - assign index after sort
      _currentPlayers = UTILS.sortPlayersByName(
        members.map(m => m.players).filter(Boolean)
      );
      _currentPlayers.forEach((p, i) => { p._index = i; });

      // Group evals by player
      _evalRecords = {};
      evals.forEach(ev => {
        if (!_evalRecords[ev.player_id]) _evalRecords[ev.player_id] = [];
        _evalRecords[ev.player_id].push(ev);
      });

      _renderPlayerList();
    } catch {
      UI.toast('Could not load players', 'error');
    }
  }

  function _renderPlayerList() {
    const list = document.getElementById('player-list');
    if (!list) return;

    if (!_currentPlayers.length) {
      UI.setEmpty('player-list', 'No players found for this team.');
      return;
    }

    list.innerHTML = _currentPlayers.map(player =>
      UI.playerListItem(
        player,
        _evalRecords[player.id] || [],
        AUTH.isDirector() || AUTH.isCoDirector(),
        AUTH.ownsEvaluation
      )
    ).join('');

    // Attach click handlers
    list.querySelectorAll('[data-player-id]').forEach(item => {
      item.addEventListener('click', () => {
        const playerId = item.dataset.playerId;
        const index    = parseInt(item.dataset.playerIndex);
        _handlePlayerTap(playerId, index);
      });
    });
  }

  function _handlePlayerTap(playerId, index) {
    const player  = _currentPlayers[index];
    const records = _evalRecords[playerId] || [];

    if (AUTH.isDirector() || AUTH.isCoDirector()) {
      _showPlayerEvalMenu(player, index);
      return;
    }

    // Coach - view/edit their own evaluation, or start a fresh one.
    const myRecord = records.find(r => AUTH.ownsEvaluation(r));
    if (myRecord) {
      UI.modal({
        title: player.name,
        body:  'You have already submitted an evaluation for this player.',
        buttons: [
          { label: 'View my evaluation', action: () => viewEvaluation(myRecord.id, index) },
          { label: 'Edit my evaluation', style: 'primary', action: () => _editEvaluation(myRecord.id, index) },
          { label: 'Cancel' },
        ],
      });
    } else {
      startEvaluation(index);
    }
  }

  // Director player menu - uniform regardless of evaluation count.
  function _showPlayerEvalMenu(player, index) {
    const records = _evalRecords[player.id] || [];
    const buttons = records.map(rec => ({
      label:  `${rec.coach_name} · ${UTILS.formatDate(rec.evaluated_at, 'short')}`,
      action: () => _showEvalActions(rec, index, player),
    }));

    buttons.push({ label: '+ Add new evaluation', style: 'primary', action: () => startEvaluation(index) });
    // One-tap averaged Director Report - skips the options modal, uses the
    // configured director weighting. Shown whenever there's ≥1 evaluation.
    if (records.length >= 1) {
      buttons.push({
        label:  '📄 Director Report (all evaluations)',
        action: () => PDF_REPORT.generateAveraged(player, _currentTeam),
      });
    }
    buttons.push({ label: 'View all-time history', action: () => showPlayerHistory(player) });
    buttons.push({ label: 'Cancel' });

    UI.modal({
      title: player.name,
      body:  records.length === 0
        ? 'No evaluations on record this season.'
        : `${records.length} evaluation${records.length === 1 ? '' : 's'} this season - select one, or add a new one.`,
      buttons,
    });
  }

  // Per-evaluation action menu - View / Edit / generate either report.
  function _showEvalActions(record, index, player) {
    UI.modal({
      title: player.name,
      body:  `Evaluation by ${record.coach_name} on ${UTILS.formatDate(record.evaluated_at, 'medium')}.`,
      buttons: [
        { label: 'View evaluation', style: 'primary', action: () => viewEvaluation(record.id, index) },
        { label: 'Edit evaluation', action: () => _editEvaluation(record.id, index) },
        { label: '📋 Player Report',   action: () => _generateReport('player', record, player) },
        { label: '📄 Director Report', action: () => _generateReport('director', record, player) },
        { label: '‹ Back', action: () => _showPlayerEvalMenu(player, index) },
      ],
    });
  }


  /**
   * The practices that train one skill, at this team's age.
   *
   * This is the join the season plan import was for: `coach_practices.skills`
   * holds the canonical ratings.skill slugs, so a weak criterion leads straight
   * to something a coach can run on Tuesday. Only published practices come back
   * (RLS), and the list is ranked most-focused first.
   */
  async function _showDrillsFor(slug, label) {
    const band = practiceBandForTeamId(_currentTeam?.id);
    if (!band) return;
    let list = [];
    try {
      list = practicesForSkill(await DB.practices.listByBands([band]), slug, band);
    } catch (err) {
      console.error('Drill lookup failed:', err);
      UI.toast(t('common.error'), 'error');
      return;
    }
    if (!list.length) { UI.toast(t('prac.drillsNone', { skill: label }), 'info', 2500); return; }

    const esc = UTILS.escapeHtml;
    const lang = CONFIG.i18n?.lang || 'en';
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" style="max-width:480px; max-height:86vh; display:flex; flex-direction:column;">
        <div class="modal__title">${esc(t('prac.drillsTitle', { skill: label }))}</div>
        <div class="modal__body" style="overflow-y:auto; min-height:0;">
          <p class="sel-detail-hint" style="margin:0 0 10px;">${esc(t('prac.drillsSub', { count: list.length }))}</p>
          ${list.slice(0, 20).map(p => `
            <button class="player-eval-card" data-drill-open="${esc(p.ref)}">
              <div class="player-eval-card__main">
                <div class="player-eval-card__title">${esc(practiceText(p, 'title', lang))}</div>
                <div class="player-eval-card__sub">
                  ${p.season_week ? esc(t('prac.week', { n: p.season_week })) : esc(t('prac.reference'))}
                  ${p.duration ? ` &middot; ${esc(p.duration)}` : ''}
                </div>
              </div>
            </button>`).join('')}
        </div>
        <div class="modal__buttons"><button class="modal-btn" data-action="close">${esc(t('common.cancel'))}</button></div>
      </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('[data-action="close"]').addEventListener('click', close);
    overlay.querySelectorAll('[data-drill-open]').forEach(b =>
      b.addEventListener('click', () => {
        const p = list.find(x => x.ref === b.dataset.drillOpen);
        if (!p) return;
        close();
        PRACTICE_VIEW.open(p, { onBack: () => UI.showScreen('screen-eval-view') });
      }));
  }

  // Fetch a single evaluation's full data and hand off to the PDF module.
  async function _generateReport(kind, record, player) {
    try {
      const { evaluation: ev, ratings } = await DB.evaluations.getById(record.id);
      if (kind === 'player') {
        // U7-U9 get the star certificate, never the pillar report: their whole
        // model is stars and encouragement, and the pillar report's team and
        // age comparisons are exactly what that age is kept away from.
        if (isYoungTeam(_currentTeam?.id)) {
          PDF_CERTIFICATE.generate(ev, ratings, player, _currentTeam);
        } else {
          PDF_PLAYER.generate(ev, ratings, player, _currentTeam);
        }
      } else {
        PDF_REPORT.generate(ev, ratings, player, _currentTeam);
      }
    } catch (err) {
      console.error('Report generation failed:', err);
      UI.toast('Could not load evaluation for report', 'error');
    }
  }

  // ── EVALUATION ────────────────────────────────────────────────────────────────

  function startEvaluation(index) {
    const player = _currentPlayers[index];
    // The youngest teams (U7-U10) get the star/ladder form, not pillar sliders.
    if (isYoungTeam(_currentTeam?.id)) {
      YOUNG_EVAL.start(player, _currentTeam, () => showPlayerList(_currentTeam));
      return;
    }
    EVALUATION.startNew(player, _currentTeam, _currentPlayers, index);
    UI.showScreen('screen-rate');
  }

  async function _editEvaluation(evalId, index) {
    try {
      const evalData = await DB.evaluations.getById(evalId);
      const player   = _currentPlayers[index];
      if (isYoungTeam(_currentTeam?.id)) {
        YOUNG_EVAL.start(player, _currentTeam, () => showPlayerList(_currentTeam), evalData);
        return;
      }
      EVALUATION.startEdit(player, _currentTeam, _currentPlayers, index, evalId, evalData);
      UI.showScreen('screen-rate');
    } catch {
      UI.toast('Could not load evaluation for editing', 'error');
    }
  }

  // ── VIEW EVALUATION ───────────────────────────────────────────────────────────

  async function viewEvaluation(evalId, index) {
    const player = _currentPlayers[index];
    UI.showScreen('screen-view-eval');
    document.getElementById('view-player-name').textContent = player.name;
    document.getElementById('view-team-name').textContent   =
      `${_currentTeam.id} · ${_currentTeam.age_group}`;
    UI.setLoading('eval-view-content');

    try {
      const { evaluation: ev, ratings } = await DB.evaluations.getById(evalId);
      const date = UTILS.formatDate(ev.evaluated_at);
      const esc = UTILS.escapeHtml;

      // Director-only: load current team ceilings so we can annotate ratings
      // that exceed them. The asterisk is hidden from coaches.
      let ceilings = [];
      if (AUTH.isDirector() && ev.team_id && ev.season_id) {
        try { ceilings = await CALIBRATION.fetchCeilingsForTeam(ev.team_id, ev.season_id); }
        catch { ceilings = []; }
      }
      const isAboveCeiling = (r) => {
        if (!AUTH.isDirector() || !ceilings.length) return false;
        if (ev.team_code === CONFIG.access.directorCode) return false; // director's own ratings excluded
        const cap = CALIBRATION.getEffectiveCeiling(ceilings, r.domain, r.skill);
        return r.level > cap;
      };

      let html = `<div class="eval-meta">Submitted by ${esc(ev.coach_name)} · ${esc(date)}</div>`;

      // Ratings by domain
      getDomains().forEach(domain => {
        const domRatings = ratings.filter(r => r.domain === domain);
        if (!domRatings.length) return;

        html += `<div class="eval-section"><div class="eval-section__title">${esc(domain)}</div>`;
        domRatings.forEach(r => {
          const above = isAboveCeiling(r);
          // A weak skill is the moment a coach most wants a drill, so the row
          // becomes a way in to the practices that train it. Only offered when
          // the season plan actually has something for that skill at this age,
          // so it never leads to an empty list, and only to someone the director
          // has given the plan to: without that check the button survives, RLS
          // returns nothing, and the coach reads "no practices train this skill"
          // when the truth is that they cannot see any of them.
          const slug = skillSlugForLabel({ skill: PRACTICE_SKILLS }, r.skill);
          const drillable = slug && r.level <= 3 && AUTH.canSeeSeasonPlans();
          html += `
            <div class="eval-row">
              <div>
                <div class="eval-row__skill">${esc(r.skill)}${above ? '<span class="above-ceiling-mark" title="Rated above current team ceiling">*</span>' : ''}</div>
                ${r.comment ? `<div class="eval-row__comment">${esc(r.comment)}</div>` : ''}
                ${drillable ? `<button class="eval-row__drills" data-drill-skill="${esc(slug)}" data-drill-label="${esc(r.skill)}">${esc(t('prac.drillsFor'))}</button>` : ''}
              </div>
              ${UI.levelBadge(r.level)}
            </div>
          `;
        });
        html += `</div>`;
      });

      // Feedback
      if (ev.strong_points || ev.working_points || ev.notes) {
        html += `<div class="eval-section"><div class="eval-section__title">Feedback</div>`;
        if (ev.strong_points)  html += `<div class="eval-text-label">Strong points</div><div class="eval-text-block">${esc(ev.strong_points)}</div>`;
        if (ev.working_points) html += `<div class="eval-text-label">Working points</div><div class="eval-text-block">${esc(ev.working_points)}</div>`;
        if (ev.notes)          html += `<div class="eval-text-label">Session notes</div><div class="eval-text-block">${esc(ev.notes)}</div>`;
        html += `</div>`;
      }

      // Edit / Delete - available to whoever owns the evaluation
      // (director on any, coach on their own).
      if (AUTH.canEditEvaluation(ev.team_code)) {
        html += `
          <div class="eval-actions">
            <button class="btn btn--ghost eval-actions__edit" id="edit-this-eval">Edit ✏️</button>
            <button class="btn btn--ghost eval-actions__delete" id="delete-this-eval">Delete 🗑</button>
          </div>
        `;
      }

      // Reports. The director gets both, each with its full options modal.
      // A coach/trainer (coach-code holder) gets only a basic Player Report
      // for their own evaluation - no options, and never a Director Report.
      if (AUTH.isDirector()) {
        html += `
          <button class="btn btn--primary eval-actions__pdf" id="generate-player-pdf-btn">📋 Player Report</button>
          <button class="btn btn--primary eval-actions__pdf" id="generate-pdf-btn">📄 Director Report</button>
        `;
      } else if (AUTH.canEditEvaluation(ev.team_code)) {
        html += `
          <button class="btn btn--primary eval-actions__pdf" id="generate-player-pdf-btn">📋 Player Report</button>
        `;
      }

      document.getElementById('eval-view-content').innerHTML = html;

      document.querySelectorAll('[data-drill-skill]').forEach(b =>
        b.addEventListener('click', () => _showDrillsFor(b.dataset.drillSkill, b.dataset.drillLabel)));

      document.getElementById('edit-this-eval')?.addEventListener('click', () => {
        _editEvaluation(evalId, index);
      });

      document.getElementById('delete-this-eval')?.addEventListener('click', () => {
        _confirmDeleteEvaluation(evalId, index);
      });

      document.getElementById('generate-pdf-btn')?.addEventListener('click', () => {
        PDF_REPORT.generate(ev, ratings, player, _currentTeam);
      });

      document.getElementById('generate-player-pdf-btn')?.addEventListener('click', () => {
        if (AUTH.isDirector()) {
          PDF_PLAYER.generate(ev, ratings, player, _currentTeam);
        } else {
          PDF_PLAYER.generateBasic(ev, ratings, player, _currentTeam);
        }
      });

    } catch {
      UI.toast('Could not load evaluation', 'error');
    }
  }

  // ── PLAYER HISTORY ────────────────────────────────────────────────────────────

  // Show the multi-season history for a player.
  async function showPlayerHistory(player) {
    UI.showScreen('screen-player-history');
    document.getElementById('history-player-name').textContent = player.name;
    document.getElementById('history-player-sub').textContent  =
      player.date_of_birth ? `Born ${UTILS.formatDate(player.date_of_birth, 'medium')}` : '';
    UI.setLoading('history-content');

    try {
      const evals = await DB.evaluations.getAllByPlayer(player.id);
      _renderPlayerHistory(player, evals);
    } catch (err) {
      console.error('History load failed:', err);
      UI.toast('Could not load history', 'error');
    }
  }

  function _renderPlayerHistory(player, evals) {
    const container = document.getElementById('history-content');
    if (!container) return;

    if (!evals.length) {
      container.innerHTML = `
        <div class="empty-state">
          <p class="empty-state__message">No evaluations on record yet.</p>
        </div>`;
      return;
    }

    const esc = UTILS.escapeHtml;

    // Group by season
    const bySeason = {};
    evals.forEach(ev => { (bySeason[ev.season_id] = bySeason[ev.season_id] || []).push(ev); });

    // Sort seasons descending (most recent first)
    const seasons = Object.keys(bySeason).sort().reverse();

    let html = '';
    seasons.forEach(seasonId => {
      const list = bySeason[seasonId];
      // Unique teams the player was in for this season
      const teams = [...new Set(list.map(e => e.team_id))].join(', ');
      const ageGroup = list[0]?.age_group || '';

      html += `
        <div class="history-season">
          <div class="history-season__header">
            <div class="history-season__season">${esc(seasonId)}</div>
            <div class="history-season__teams">${esc(teams)}${ageGroup ? ' · ' + esc(ageGroup) : ''}</div>
          </div>
          ${list.map(ev => `
            <div class="history-eval" data-eval-id="${esc(ev.id)}">
              <div class="history-eval__main">
                <div class="history-eval__date">${esc(UTILS.formatDate(ev.evaluated_at, 'medium'))}</div>
                <div class="history-eval__coach">By ${esc(ev.coach_name)} · ${esc(ev.team_id)}</div>
              </div>
              <div class="history-eval__chevron">›</div>
            </div>
          `).join('')}
        </div>
      `;
    });

    container.innerHTML = html;

    // Click any evaluation row -> open the existing view-eval screen
    container.querySelectorAll('[data-eval-id]').forEach(row => {
      row.addEventListener('click', () => _openHistoryEval(row.dataset.evalId, player));
    });
  }

  async function _openHistoryEval(evalId, player) {
    UI.showScreen('screen-view-eval');
    document.getElementById('view-player-name').textContent = player.name;
    document.getElementById('view-team-name').textContent   = '';
    UI.setLoading('eval-view-content');
    // Reuse the same renderer as the normal viewEvaluation flow.
    // We pass index = -1 to flag "came from history" so back goes to history, not the player list.
    _historyContext = { player };
    try {
      // Inline the rendering logic - viewEvaluation expects an index into _currentPlayers,
      // which won't be valid for cross-season history. Render here instead.
      const { evaluation: ev, ratings } = await DB.evaluations.getById(evalId);
      const date = UTILS.formatDate(ev.evaluated_at);
      const esc  = UTILS.escapeHtml;

      // Director-only above-ceiling annotation (same as viewEvaluation).
      let ceilings = [];
      if (AUTH.isDirector() && ev.team_id && ev.season_id) {
        try { ceilings = await CALIBRATION.fetchCeilingsForTeam(ev.team_id, ev.season_id); }
        catch { ceilings = []; }
      }
      const isAboveCeiling = (r) => {
        if (!AUTH.isDirector() || !ceilings.length) return false;
        if (ev.team_code === CONFIG.access.directorCode) return false;
        const cap = CALIBRATION.getEffectiveCeiling(ceilings, r.domain, r.skill);
        return r.level > cap;
      };

      let html = `<div class="eval-meta">Submitted by ${esc(ev.coach_name)} · ${esc(date)} · ${esc(ev.team_id)}</div>`;

      getDomains().forEach(domain => {
        const domRatings = ratings.filter(r => r.domain === domain);
        if (!domRatings.length) return;
        html += `<div class="eval-section"><div class="eval-section__title">${esc(domain)}</div>`;
        domRatings.forEach(r => {
          const above = isAboveCeiling(r);
          html += `
            <div class="eval-row">
              <div>
                <div class="eval-row__skill">${esc(r.skill)}${above ? '<span class="above-ceiling-mark" title="Rated above current team ceiling">*</span>' : ''}</div>
                ${r.comment ? `<div class="eval-row__comment">${esc(r.comment)}</div>` : ''}
              </div>
              ${UI.levelBadge(r.level)}
            </div>`;
        });
        html += `</div>`;
      });

      if (ev.strong_points || ev.working_points || ev.notes) {
        html += `<div class="eval-section"><div class="eval-section__title">Feedback</div>`;
        if (ev.strong_points)  html += `<div class="eval-text-label">Strong points</div><div class="eval-text-block">${esc(ev.strong_points)}</div>`;
        if (ev.working_points) html += `<div class="eval-text-label">Working points</div><div class="eval-text-block">${esc(ev.working_points)}</div>`;
        if (ev.notes)          html += `<div class="eval-text-label">Session notes</div><div class="eval-text-block">${esc(ev.notes)}</div>`;
        html += `</div>`;
      }

      // Director (or whoever submitted it) gets a Delete button. Edit isn't
      // wired here because editing would require routing into the eval flow
      // with a player object that may belong to a past season - out of scope.
      if (AUTH.canEditEvaluation(ev.team_code)) {
        html += `
          <div class="eval-actions">
            <button class="btn btn--ghost eval-actions__delete" id="delete-history-eval">Delete evaluation 🗑</button>
          </div>
        `;
      }

      document.getElementById('eval-view-content').innerHTML = html;

      document.getElementById('delete-history-eval')?.addEventListener('click', () => {
        _confirmDeleteHistoryEval(evalId, ev, player);
      });
    } catch {
      UI.toast('Could not load evaluation', 'error');
    }
  }

  // Delete an evaluation from the Player History flow.
  function _confirmDeleteHistoryEval(evalId, ev, player) {
    const date = UTILS.formatDate(ev.evaluated_at, 'medium');
    UI.modal({
      title: 'Delete this evaluation?',
      body:  `Permanently remove the ${date} evaluation by ${ev.coach_name} (${ev.team_id}) for ${player.name}.\n\nThis cannot be undone. The player's other evaluations are unaffected.`,
      buttons: [
        {
          label: 'Yes, delete',
          style: 'danger',
          action: async () => {
            try {
              await DB.evaluations.delete(evalId);
              UI.toast('Evaluation deleted', 'success');
              _historyContext = null;
              showPlayerHistory(player);
            } catch (err) {
              console.error('Delete failed:', err);
              UI.toast('Could not delete evaluation', 'error');
            }
          },
        },
        { label: 'Cancel' },
      ],
    });
  }

  let _historyContext = null;

  // ── DELETE EVALUATION ─────────────────────────────────────────────────────────

  function _confirmDeleteEvaluation(evalId, index) {
    const player = _currentPlayers[index];
    UI.modal({
      title: 'Delete evaluation',
      body: `Delete this evaluation for ${player?.name || 'this player'}? This cannot be undone.`,
      buttons: [
        {
          label: 'Delete',
          style: 'danger',
          action: async () => {
            try {
              await DB.evaluations.delete(evalId);
              UI.toast('Evaluation deleted', 'success');
              // Refresh the player list to reflect removal
              await showPlayerList();
            } catch (err) {
              console.error('Delete failed:', err);
              UI.toast('Could not delete evaluation', 'error');
            }
          },
        },
        { label: 'Cancel' },
      ],
    });
  }

  // ── SIGN OUT ──────────────────────────────────────────────────────────────────

  // Clear the session and reload to the gate screen.
  // Clear the session and reload to the gate screen. Reachable from the
  // director home and the coach banner. If there are in-progress evaluation
  // drafts, surface the count so the coach can finish them first if they want.
  function signOut() {
    const code   = AUTH.getCode();
    const drafts = code ? DRAFT.findForCode(code) : [];
    const body = drafts.length
      ? `You have ${drafts.length} unsaved evaluation draft${drafts.length === 1 ? '' : 's'} on this device. ` +
        'They\'ll stay saved here and be waiting when you sign back in with the same code - but if this is a shared phone, finish them first.'
      : 'You will be returned to the access-code screen. Any saved drafts on this device are kept.';
    UI.modal({
      title: drafts.length ? 'Sign out with drafts saved?' : 'Sign out?',
      body,
      buttons: [
        { label: 'Stay signed in', style: 'primary' },
        {
          label: 'Sign out',
          style: 'danger',
          action: () => {
            AUTH.clearSession();
            const url = new URL(window.location.href);
            url.search = '';
            history.replaceState(null, '', url.toString());
            window.location.reload();
          },
        },
      ],
    });
  }

  // ── EVENT LISTENERS ───────────────────────────────────────────────────────────

  function _bindEvents() {

    // Gate
    document.getElementById('gate-submit')?.addEventListener('click', _submitGate);
    document.getElementById('gate-input')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') _submitGate();
    });

    // Name
    document.getElementById('name-submit')?.addEventListener('click', _submitName);
    document.getElementById('name-input')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') _submitName();
    });

    // Home navigation
    document.getElementById('btn-dashboard')?.addEventListener('click', () => DASHBOARD.showOverview());
    document.getElementById('btn-codes')?.addEventListener('click', () => DASHBOARD.showCodeManager());
    document.getElementById('btn-squad')?.addEventListener('click', () => DASHBOARD.showSquadManager());
    document.getElementById('btn-selections')?.addEventListener('click', () => SELECTION.showList());
    document.getElementById('btn-calibrate')?.addEventListener('click', () => CALIBRATION.showList());
    document.getElementById('btn-player-access')?.addEventListener('click', () => PLAYER_ACCESS.show());
    document.getElementById('btn-share-dash')?.addEventListener('click', () => SHARE_DASH.show());
    document.getElementById('btn-team-files')?.addEventListener('click', () => TEAM_FILES.show());
    document.getElementById('btn-grade-panel')?.addEventListener('click', () => GRADING_PANEL.show());
    document.getElementById('back-from-grade-panel')?.addEventListener('click', showHome);
    document.getElementById('btn-squad-replies')?.addEventListener('click', () => SQUAD_REPLIES.show());
    document.getElementById('btn-staff-access')?.addEventListener('click', () => STAFF_ACCESS.show(showHome));
    document.getElementById('btn-baseline')?.addEventListener('click', () => BASELINE.show());
    document.getElementById('btn-ranking')?.addEventListener('click', () => RANKING.show());
    document.getElementById('btn-squad-grid')?.addEventListener('click', () => SQUAD_GRID.show());
    document.getElementById('btn-health')?.addEventListener('click', () => HEALTH.show());
    document.getElementById('btn-tracking')?.addEventListener('click', () => PLAYER_HISTORY.show());
    document.getElementById('btn-selection-analysis')?.addEventListener('click', () => SELECTION_REPORT.show());
    document.getElementById('btn-master-analysis')?.addEventListener('click', () => MASTER_ANALYSIS_REPORT.show());
    document.getElementById('btn-signout-home')?.addEventListener('click', signOut);

    // Gate static text (translated) + the EN/NL language toggle. Works pre-login
    // so a parent or player entering a code can switch language first.
    const _gt = (id, key) => { const el = document.getElementById(id); if (el) el.textContent = t(key); };
    _gt('gate-subtitle', 'gate.subtitle');
    _gt('gate-codelabel', 'gate.codeLabel');
    _gt('gate-submit', 'gate.continue');
    const gateInput = document.getElementById('gate-input');
    if (gateInput) gateInput.placeholder = t('gate.codePlaceholder');
    UI.wireLangToggle(document.getElementById('gate-lang'));

    // Module 1 - parent landing page + player screens.
    const parentLink = document.getElementById('gate-parent-link');
    if (parentLink) {
      parentLink.textContent = t('parent.gateLink');
      parentLink.addEventListener('click', () => PLAYER_PAGE.showParentRequest());
    }
    document.getElementById('back-from-parent-request')?.addEventListener('click', () => UI.showScreen('screen-gate'));
    document.getElementById('parent-submit')?.addEventListener('click', () => PLAYER_PAGE.submitParentRequest());
    document.getElementById('parent-confirm-done')?.addEventListener('click', () => UI.showScreen('screen-gate'));

    // Coach/trainer self-apply (public) - mirrors the parent apply.
    const staffLink = document.getElementById('gate-staff-link');
    if (staffLink) {
      staffLink.textContent = t('staffApply.gateLink');
      staffLink.addEventListener('click', () => STAFF_APPLY.showRequest());
    }
    document.getElementById('back-from-staff-request')?.addEventListener('click', () => UI.showScreen('screen-gate'));
    document.getElementById('staff-submit')?.addEventListener('click', () => STAFF_APPLY.submit());
    document.getElementById('staff-confirm-done')?.addEventListener('click', () => UI.showScreen('screen-gate'));
    document.getElementById('back-from-staff-access')?.addEventListener('click', showHome);
    document.getElementById('back-from-player-eval')?.addEventListener('click', () => PLAYER_PAGE.showHome());
    document.getElementById('back-from-player-intro')?.addEventListener('click', () => PLAYER_PAGE.showHome());
    document.getElementById('back-from-player-baseline')?.addEventListener('click', () => PLAYER_PAGE.showHome());
    document.getElementById('back-from-player-baseline-progress')?.addEventListener('click', () => PLAYER_PAGE.showHome());
    document.getElementById('back-from-player-selftest')?.addEventListener('click', () => PLAYER_PAGE.showHome());
    document.getElementById('back-from-player-reflect')?.addEventListener('click', () => PLAYER_PAGE.showHome());
    document.getElementById('player-signout')?.addEventListener('click', signOut);
    document.getElementById('back-from-player-access')?.addEventListener('click', showHome);
    document.getElementById('back-from-share-dash')?.addEventListener('click', showHome);
    document.getElementById('back-from-team-files')?.addEventListener('click', showHome);
    document.getElementById('back-from-squad-replies')?.addEventListener('click', showHome);

    // Module 2 - Baseline Testing (director screens + tester sign-out).
    document.getElementById('back-from-ranking')?.addEventListener('click', showHome);
    document.getElementById('back-from-squad-grid')?.addEventListener('click', showHome);
    // Tracking can be reached from the home menu or from the squad grid, so it
    // decides where back goes, including falling back to home itself. Do not
    // test the return value here: PLAYER_HISTORY is lazy-loaded, so its methods
    // return promises and any `if (!...)` around this is always false.
    document.getElementById('back-from-tracking')?.addEventListener('click', () => PLAYER_HISTORY.goBack());
    document.getElementById('back-from-health')?.addEventListener('click', showHome);
    document.getElementById('back-from-game-picks')?.addEventListener('click', () => showPlayerList(_currentTeam));
    document.getElementById('back-from-baseline-list')?.addEventListener('click', showHome);
    document.getElementById('back-from-baseline-wizard')?.addEventListener('click', () => BASELINE.show());
    document.getElementById('back-from-baseline-detail')?.addEventListener('click', () => BASELINE.show());
    document.getElementById('back-from-baseline-results')?.addEventListener('click', () => BASELINE.backFromResults());
    // A coach who entered testing from the Coach Hub returns there; a BT-
    // helper signs out. The tester module decides which applies.
    document.getElementById('baseline-tester-signout')?.addEventListener('click', () => {
      if (!BASELINE_TESTER.exitToHubIfStaff()) signOut();
    });
    document.getElementById('back-from-baseline-queue')?.addEventListener('click', () => BASELINE_TESTER.backToHome());
    document.getElementById('back-from-baseline-test')?.addEventListener('click', () => BASELINE_TESTER.backToQueue());

    // Module 6 - Coach Hub screens.
    document.getElementById('back-from-staff-eval-list')?.addEventListener('click', () => STAFF_EVAL.backFromList());
    document.getElementById('back-from-staff-eval-edit')?.addEventListener('click', () => STAFF_EVAL.backFromEditor());
    document.getElementById('back-from-lineup')?.addEventListener('click', () => LINEUP.backFromList());
    document.getElementById('back-from-lineup-edit')?.addEventListener('click', () => LINEUP.backFromEditor());
    document.getElementById('back-from-reflect')?.addEventListener('click', () => SELF_REFLECTION.backFromList());
    document.getElementById('back-from-reflect-review')?.addEventListener('click', () => SELF_REFLECTION.backFromReview());
    document.getElementById('back-from-staff-notes')?.addEventListener('click', () => STAFF_NOTES.backFrom());
    document.getElementById('back-from-chub-reflection')?.addEventListener('click', () => COACH_HUB.showHome());
    document.getElementById('back-from-chub-eval-view')?.addEventListener('click', () => COACH_HUB.showHome());
    document.getElementById('coach-hub-signout')?.addEventListener('click', signOut);
    document.getElementById('back-from-dta-admin')?.addEventListener('click', () => DTA_ADMIN.backFrom());
    // The article screen is reachable from the coach Library AND the director's
    // Preview, so the viewer remembers which one opened it.
    document.getElementById('back-from-dta-article')?.addEventListener('click', () => DTA_ARTICLE.back());
    // Same for a season plan practice: the coach's plan and the director's
    // preview both land on this screen.
    document.getElementById('back-from-practice')?.addEventListener('click', () => PRACTICE_VIEW.back());
    document.getElementById('back-from-exercise')?.addEventListener('click', () => EXERCISE_VIEW.back());
    // Leaving the writing screen goes back to the hub; the draft is only ever
    // stored when the coach presses save, so there is nothing to lose here.
    document.getElementById('back-from-exercise-edit')?.addEventListener('click', () => COACH_HUB.showHome());
    const accessTitle = document.getElementById('player-access-title');
    if (accessTitle) {
      accessTitle.textContent = t('access.title');
      document.getElementById('player-access-sub').textContent = t('access.subtitle');
    }

    // Calibration back navigation
    document.getElementById('back-from-calibrate-list')?.addEventListener('click', showHome);
    document.getElementById('back-from-calibrate-team')?.addEventListener('click', () => CALIBRATION.showList());

    // Selection screen back buttons
    document.getElementById('back-from-selection-list')?.addEventListener('click', showHome);
    document.getElementById('back-from-selection-detail')?.addEventListener('click', () => SELECTION.showList());
    document.getElementById('back-from-selection-trainers')?.addEventListener('click', () => SELECTION.showList());
    document.getElementById('back-from-selection-report')?.addEventListener('click', () => {
      SELECTION.leaveReport();
      // A view-only trainer reaches this screen too - they go back to their
      // session picker, not the director's selections list.
      if (AUTH.isDirector()) SELECTION.showList();
      else                   SELECTION_COACH.showSessions();
    });
    document.getElementById('back-from-selection-grid')?.addEventListener('click', () => SELECTION_COACH.showSessions());
    document.getElementById('back-from-selection-rate')?.addEventListener('click', () => SELECTION_COACH.show());
    document.getElementById('back-from-selection-roster-check')?.addEventListener('click', () => SELECTION_COACH.show());
    document.getElementById('back-from-selection-admin')?.addEventListener('click', () => SELECTION_COACH.showSessions());
    document.getElementById('back-from-selection-attendance')?.addEventListener('click', () => SELECTION.backFromAttendance());
    document.getElementById('back-from-selection-bronco')?.addEventListener('click', () => SELECTION_COACH.showSessions());
    document.getElementById('back-from-selection-bronco-report')?.addEventListener('click', () => SELECTION.backFromBronco());
    document.getElementById('sel-bronco-report-export')?.addEventListener('click', () => SELECTION.exportBroncoCsv());
    document.getElementById('btn-undo-mark')?.addEventListener('click', () => SELECTION_COACH.undo());
    document.getElementById('btn-flag-bib')?.addEventListener('click',  () => SELECTION_COACH.flag());

    // Back buttons
    document.getElementById('back-from-players')?.addEventListener('click', () => {
      if (AUTH.isDirector() || AUTH.isCoDirector()) showHome();
      else if (AUTH.isStaff()) COACH_HUB.showHome();
    });

    document.getElementById('back-from-rate')?.addEventListener('click', () => {
      EVALUATION.goBack();
    });

    document.getElementById('back-from-young-eval')?.addEventListener('click', () => showPlayerList(_currentTeam));

    document.getElementById('back-from-view')?.addEventListener('click', () => {
      if (_historyContext) {
        const player = _historyContext.player;
        _historyContext = null;
        showPlayerHistory(player);
      } else {
        showPlayerList();
      }
    });
    document.getElementById('back-from-history')?.addEventListener('click', () => showPlayerList());
    document.getElementById('back-from-dashboard')?.addEventListener('click', showHome);
    document.getElementById('back-from-codes')?.addEventListener('click', showHome);
    document.getElementById('back-from-squad')?.addEventListener('click', showHome);
    document.getElementById('btn-coaches')?.addEventListener('click', () => STAFF.show());  // the one route to the coaching hub
    document.getElementById('btn-rubric')?.addEventListener('click', () => RUBRIC_EDITOR.show());
    document.getElementById('back-from-rubric-editor')?.addEventListener('click', showHome);
    document.getElementById('btn-dta')?.addEventListener('click', () => DTA_ADMIN.show(showHome));  // the one route to the DTA library
    document.getElementById('back-from-staff')?.addEventListener('click', () => showHome());

    // Evaluation screen
    document.getElementById('btn-prev-player')?.addEventListener('click', () => EVALUATION.prevPlayer());
    document.getElementById('btn-next-player')?.addEventListener('click', () => EVALUATION.nextPlayer());
    document.getElementById('save-btn')?.addEventListener('click', () => EVALUATION.submit());
    document.getElementById('draft-clear')?.addEventListener('click', () => EVALUATION.clearDraft());
    document.getElementById('rate-reference-toggle')?.addEventListener('click', () => EVALUATION.toggleReference());

    // Modal overlay click-outside to close
    document.getElementById('modal-overlay')?.addEventListener('click', e => {
      if (e.target === document.getElementById('modal-overlay')) UI.closeModal();
    });

    // Twizzit file import
    document.getElementById('btn-import-twizzit')?.addEventListener('click', () => {
      document.getElementById('twizzit-file-input')?.click();
    });

    document.getElementById('twizzit-file-input')?.addEventListener('change', async e => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        await DASHBOARD.processImport(text);
      } catch {
        UI.toast('Could not read file', 'error');
      }
      e.target.value = ''; // reset input
    });

    // Code manager buttons - open proper generator screen
    document.getElementById('btn-gen-season')?.addEventListener('click', () => CODE_GENERATOR.show('season'));
    document.getElementById('btn-gen-special')?.addEventListener('click', () => CODE_GENERATOR.show('team'));
    document.getElementById('btn-plan-season')?.addEventListener('click', () => PLANNER.show());

    // Season planner - back to the code manager.
    document.getElementById('back-from-planner')?.addEventListener('click', () => DASHBOARD.showCodeManager());

    // Code generator screen
    CODE_GENERATOR.bindEvents();
  }

  // ── PUBLIC API ────────────────────────────────────────────────────────────────

  return {
    boot,
    // Drop the director's personal "I am looking at season X" choice, so the
    // next boot follows the database again. The Dashboard calls this whenever
    // it makes a season live for the whole club: without it the director keeps
    // seeing their saved season and cannot tell that the club moved, which is
    // exactly how the club sat on 2025-2026 unnoticed for two weeks.
    clearSeasonOverride() {
      try { localStorage.removeItem(SEASON_PREF_KEY); } catch { /* no storage */ }
    },
    showHome,
    showPlayerList,
    startEvaluation,
    viewEvaluation,
    showPlayerHistory,
    signOut,
    _bindEvents,  // called by DOMContentLoaded below
  };

})();

// ── INIT ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  APP._bindEvents();
  APP.boot();
});
